import { useEffect, useRef } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/esm/Button.js'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import ConfigTemplateNav from './components/ConfigTemplateNav.jsx'
import DebugAccordion from '../../components/DebugAccordion'
import LoadingAnimation from '../../components/LoadingAnimation'
import PageTitle from '../../components/PageTitle.jsx'

import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'
import { addNotification, NotificationTypes } from '../../state/actions/notificationActions'

import { configTemplateSignal, configTemplateLoadedSignal } from '../../state/signals/configTemplateSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ConfigTemplate() {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, configTemplateId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates/`)
    const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditConfigTemplates
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchConfigTemplate(beId, configTemplateId, config, accessToken)
        fetchSchemaManifest()
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    const formRef = useRef(null)
    const configTemplateNameSignal = useSignal('')
    const configTemplateVersionSignal = useSignal('')
    const configTemplateJsonSignal = useSignal('')

    useSignalEffect(() => {
        configTemplateNameSignal.value = configTemplateSignal.value?.name ?? ''
        configTemplateVersionSignal.value = configTemplateSignal.value?.utgSchemaVersion ?? ''
        configTemplateJsonSignal.value = configTemplateSignal.value?.config ? JSON.stringify(JSON.parse(configTemplateSignal.value.config), null, 2) : '{}'
    })

    const schemaVersionOptionsComputed = useComputed(() => {
        const configs = schemaManifestSignal.value?.configs
        if (!configs?.length) return []
        return [...configs]
            .sort((a, b) => a.utgVersion < b.utgVersion ? 1 : -1)
            .map(({ utgVersion }) => (
                <option value={utgVersion} key={utgVersion}>{utgVersion}</option>
            ))
    })
    const handleInput = (event, signalVariable) => {
        //console.log(event.currentTarget.value)
        signalVariable.value = event.currentTarget.value
        configTemplateSignal.value.name = configTemplateNameSignal.value
        configTemplateSignal.value.config = configTemplateJsonSignal.value
    }

    const accordionData = [
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleSave = (event) => {
        event.preventDefault()
        const errorCallback = (error) => {
            addNotification('Unable to update the config template: '.concat(error.message), NotificationTypes.ERROR, false)
            return false
        }
        try {
            configTemplateSignal.value.config = JSON.stringify(JSON.parse(configTemplateJsonSignal.value), null, 0)
            updateConfigTemplate(beId, configTemplateSignal.value, config, accessToken).then(
                (result) => {
                    configTemplateSignal.value = result
                    addNotification('The config template was successfully updated.', NotificationTypes.SUCCESS, true)
                }
            ).catch(
                (error) => {
                    errorCallback(error)
                }
            )
        }
        catch (error) {
            return errorCallback(error)
        }
    }



    if (!configTemplateSignal.value || !configTemplateLoadedSignal.value) {
        return (<LoadingAnimation />)
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Template List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Manage {configTemplateSignal.value.name}</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Manage Config Template' canEditSignal={canEditComputed} />
            </Stack>
            <Form className="px-3 mb-2" onSubmit={e => {
                e.preventDefault()
                updateConfigTemplate(beId, { ...configTemplateSignal.value, name: configTemplateNameSignal.value, utgSchemaVersion: configTemplateVersionSignal.value }, config, accessToken)
                    .then(result => { configTemplateSignal.value = result; addNotification('Config template updated.', NotificationTypes.SUCCESS, true) })
                    .catch(error => addNotification(`Unable to update. ${error.toString()}`, NotificationTypes.ERROR, false))
            }}>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateName">
                    <Form.Label column sm="2" className="text-end">Name:</Form.Label>
                    <Col sm="10">
                        <Form.Control value={configTemplateNameSignal.value} onInput={(event) => handleInput(event, configTemplateNameSignal)} required type="text" readOnly={!canEditComputed.value} />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateVersion">
                    <Form.Label column sm="2" className="text-end">Schema Version:</Form.Label>
                    <Col sm="8">
                        <Form.Control as="select" value={configTemplateVersionSignal.value} onChange={e => configTemplateVersionSignal.value = e.target.value} disabled={!canEditComputed.value}>
                            <option value="">Select Schema Version</option>
                            {schemaVersionOptionsComputed.value}
                        </Form.Control>
                    </Col>
                    {canEditComputed.value && (
                        <Col sm="2">
                            <button type="submit" className="btn btn-primary btn-sm">Save</button>
                        </Col>
                    )}
                </Form.Group>
            </Form>
            <ConfigTemplateNav activeKey="1" />

            <Form ref={formRef} validated onSubmit={(event) => handleSave(event)}>

                <Form.Group as={Row} className="mb-3" controlId="ConfigTemplateJson">
                    <Form.Label column sm="2" className="text-end">JSON:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control as="textarea" row="30" style={{ height: '500px' }} defaultValue={configTemplateJsonSignal.value} onInput={(event) => handleInput(event, configTemplateJsonSignal)} required type="text" readOnly={!canEditComputed.value} />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                {
                    canEditComputed.value && (
                        <Form.Group as={Row} className="mb-3" controlId="formUpdateButton">
                            <Form.Label column sm="2" className="text-end"></Form.Label>
                            <Col sm="10">
                                <Stack direction="horizontal" gap={3}>
                                    <Button type="submit">Update</Button>
                                    <Button variant="secondary" onClick={() => navigate(0)}>Cancel</Button>
                                </Stack>
                            </Col>
                        </Form.Group>
                    )
                }
            </Form>

            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}